import { h, hydrate, render, type ComponentType } from "preact";
import { type AttrSchema, parseAttrs, type PropsOf } from "./attrs";
import { ErrorBoundary, defaultFallback } from "./error-boundary";
import { isMounted, markMounted, unmark } from "./registry";
import { injectStyles, setupShadow } from "./shadow";

// Replaced by Vite at build time via `define` (see packages/config/src/vite.ts).
// Tests get a literal value from vitest.config.ts.
declare const __WIDGET_VERSION__: string;

export interface WidgetErrorInfo {
  /** Widget name as registered via `defineWidget`. */
  name: string;
  /** The host element (`<div data-widget="…">`) that failed. */
  host: Element;
  /** The thrown error. */
  error: Error;
}

export interface DefineWidgetConfig<S extends AttrSchema> {
  /** kebab-case identifier matching `data-widget="<name>"`. */
  name: string;
  /** Preact component rendered inside the shadow root. */
  component: ComponentType<PropsOf<S>>;
  /** Schema describing the `data-*` attributes the widget accepts. */
  attrs: S;
  /** CSS string injected into the shadow root (typically imported with `?inline`). */
  styles: string;
  /**
   * Called when the widget throws during attribute parsing, shadow setup, or
   * (synchronous) Preact rendering. Defaults to `console.error`. Use this to
   * forward errors to a reporting endpoint. The widget shows a German fallback
   * UI in the host's shadow root either way.
   */
  onError?: (info: WidgetErrorInfo) => void;
}

const onReady = (cb: () => void): void => {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", cb, { once: true });
  } else {
    cb();
  }
};

const reportError = <S extends AttrSchema>(
  config: DefineWidgetConfig<S>,
  host: Element,
  error: unknown,
): void => {
  const err = error instanceof Error ? error : new Error(String(error));
  const handler =
    config.onError ??
    ((info: WidgetErrorInfo): void => {
      console.error(`[widget:${info.name}]`, info.error);
    });
  try {
    handler({ name: config.name, host, error: err });
  } catch (handlerError) {
    console.error(`[widget:${config.name}] onError handler threw`, handlerError);
  }
};

const renderFallback = (shadow: ShadowRoot): void => {
  render(defaultFallback(), shadow);
};

const warnVersionDrift = <S extends AttrSchema>(
  config: DefineWidgetConfig<S>,
  host: Element,
): void => {
  const snippetVersion = host.getAttribute("data-skeleton-version");
  if (snippetVersion === null || snippetVersion === __WIDGET_VERSION__) return;
  console.warn(
    `[widget:${config.name}] embed snippet was generated with widget version ${snippetVersion}, ` +
      `current bundle is ${__WIDGET_VERSION__}. ` +
      `Re-generate the snippet from the configurator to avoid hydration drift.`,
  );
};

const mountInstance = <S extends AttrSchema>(
  host: Element,
  config: DefineWidgetConfig<S>,
): void => {
  if (isMounted(host)) return;
  markMounted(host);
  // Declarative Shadow DOM: when the embed snippet contains
  // `<template shadowrootmode="open">`, the browser has already attached a
  // shadow root and populated it during HTML parsing. We then hydrate the
  // existing DOM instead of re-rendering, so there's no visual flash.
  const existingShadow = host.shadowRoot;
  let shadow: ShadowRoot;
  try {
    if (existingShadow !== null) {
      shadow = existingShadow;
      // The snippet's <style> reflects the CSS at snippet-generation time. Layer
      // current styles on top so they win over stale ones — protects against
      // CSS drift when an old snippet is still embedded somewhere.
      injectStyles(shadow, config.styles);
      warnVersionDrift(config, host);
    } else {
      shadow = setupShadow(host, config.styles);
    }
  } catch (error) {
    reportError(config, host, error);
    return;
  }
  try {
    const props = parseAttrs(host, config.attrs);
    const tree = h(
      ErrorBoundary,
      {
        fallback: defaultFallback(),
        onError: (error) => {
          reportError(config, host, error);
        },
      },
      h(config.component, props),
    );
    if (existingShadow !== null) {
      hydrate(tree, shadow);
    } else {
      render(tree, shadow);
    }
  } catch (error) {
    reportError(config, host, error);
    renderFallback(shadow);
  }
};

const scanAndMount = <S extends AttrSchema>(config: DefineWidgetConfig<S>): void => {
  const selector = `[data-widget="${config.name}"]`;
  const hosts = document.querySelectorAll<HTMLElement>(selector);
  for (const host of hosts) {
    mountInstance(host, config);
  }
};

const observeNewHosts = <S extends AttrSchema>(config: DefineWidgetConfig<S>): void => {
  const selector = `[data-widget="${config.name}"]`;
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches(selector)) {
          mountInstance(node, config);
        }
        const nested = node.querySelectorAll<HTMLElement>(selector);
        for (const host of nested) {
          mountInstance(host, config);
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
};

/**
 * Register and mount a widget. Scans the DOM for `[data-widget="<name>"]`
 * elements, attaches a shadow root to each, and renders the component into it.
 *
 * Idempotent: safe to call multiple times (e.g. if the script is loaded twice).
 * Multi-instance: each matched element gets its own shadow root and Preact tree.
 * Reactive: a `MutationObserver` mounts widgets that are added to the DOM later
 * (e.g. when a Webflow tab becomes visible).
 *
 * Errors thrown during attribute parsing, shadow setup, or component rendering
 * are caught — the host shows a German fallback UI and `onError` is called.
 */
export const defineWidget = <S extends AttrSchema>(config: DefineWidgetConfig<S>): void => {
  onReady(() => {
    scanAndMount(config);
    observeNewHosts(config);
  });
};

/** Manually unmount a widget from a host (e.g. for SPA-style navigation). */
export const unmount = (host: Element): void => {
  if (host.shadowRoot) {
    render(null, host.shadowRoot);
  }
  unmark(host);
};
